import React, { useState, useEffect, useRef, useMemo } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import useScoFormsEnabled from "../../hooks/useScoFormsEnabled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getUser,
  updateDipUser,
  getAllRoles,
  getScoFormSettings,
  getUsersByTenant,
} from "../../api/Users";
import { getAccountTypesByAgreement } from "../../api/Agreement";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import UserForm from "../../components/user/UserForm";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByUser } from "../../api/BillingGroups"; // Import the API function
import { getScoCustomQuestions, getScoFormValues } from "../../api/Tenants";
import { offeredListValues } from "../../components/user/listValues";
import {
  getGroupOptionsByTenant,
  getUserGroupMemberships,
} from "../../api/EntraGroups";
import { getAddOnLicenseOptionsByTenant } from "../../api/Licenses";

/**
 * Distinct, sorted values of one field among the customer's users. Mirrors the helper
 * the onboarding form uses (PBI #292), so both forms offer the same values.
 */
const distinctValues = (users, field) => {
  const values = users
    .map((user) => user[field])
    .filter((value) => typeof value === "string" && value.trim() !== "");

  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
};

/**
 * Meeting rooms, resources and shared mailboxes are ordinary Entra accounts without licences
 * and must not offer their Office value (#352). Mirrors the onboarding form: only an empty
 * licence map counts — a tenant without licence data (no map at all) is not filtered.
 */
const isUnlicensedAccount = (user) =>
  !!user?.licenses && Object.keys(user.licenses).length === 0;

/**
 * Combines the values configured for the customer with the ones its users already use.
 */
const combineOptions = (configured, inUse) => [
  ...new Set([...(configured || []), ...inUse]),
];

const EditUserPage = () => {
  const { getAccessToken } = useAuth();
  const { agreementPublicId, tenantPublicId, userPublicId } = useParams();
  const { enabled, loaded } = useScoFormsEnabled(agreementPublicId);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);
  const [roles, setRoles] = useState([]);

  // PBI #316/#318: the customer's users (manager choices) and account types.
  const [users, setUsers] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);

  // The chosen manager, as a public id. Empty means unchanged — only a made choice is sent
  // to the API. The account type cannot be changed here (PBI #380).
  const [managerUserPublicId, setManagerUserPublicId] = useState("");

  // PBI #320: the customer's own questions and the answers given in THIS edit, keyed
  // by question public id. Answers are never stored — they only travel in the
  // verification mail — so the fields always start empty.
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customAnswers, setCustomAnswers] = useState({});

  // PBI #317 and #292: wording, the support address and the list-value policy are served
  // by the API so they are not hardcoded here and can be changed without a release.
  const [scoFormSettings, setScoFormSettings] = useState({});

  // #357: the customer's saved Office, Company and Department values, as on the onboarding form.
  const [scoListValues, setScoListValues] = useState([]);

  // PBI #372: the customer's Entra groups and the ones this user is ticked in. The groups
  // are sent only once the customer has changed a tick — an omitted field leaves the
  // memberships untouched, while an empty list would remove every one of them.
  const [groupOptions, setGroupOptions] = useState(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupsChanged, setGroupsChanged] = useState(false);

  useEffect(() => {
    handleGetUser();
    handleGetBillingGroups(); // Fetch billing groups
    handleGetRoles(); // Fetch roles
    handleGetScoFormSettings();
    handleGetUsers();
    handleGetAccountTypes();
    handleGetCustomQuestions();
    handleGetScoListValues();
    handleGetGroups();
    handleGetAddOnLicenses();
  }, [agreementPublicId, tenantPublicId, userPublicId]);

  /**
   * PBI #372: ticks the user's current groups, limited to the ones the picker offers (as
   * the onboarding copy does). Without the current memberships the ticks would misstate
   * them, so a failed membership read leaves only the status message — or no picker.
   */
  const handleGetGroups = async () => {
    const accessToken = await getAccessToken();
    const [options, memberships] = await Promise.allSettled([
      getGroupOptionsByTenant(tenantPublicId, accessToken),
      getUserGroupMemberships(userPublicId, accessToken),
    ]);

    if (options.status !== "fulfilled" || !options.value) {
      console.error("Failed to fetch Entra group options:", options.reason?.message);
      return;
    }

    const offered = options.value.groups || [];

    if (memberships.status !== "fulfilled") {
      console.error("Failed to fetch group memberships:", memberships.reason?.message);
      if (offered.length === 0) setGroupOptions(options.value);
      return;
    }

    const offeredIds = new Set(offered.map((group) => group.entraGroupPublicId));
    setSelectedGroupIds(
      (memberships.value?.entraGroupPublicIds || []).filter((id) => offeredIds.has(id))
    );
    setGroupOptions(options.value);
  };

  const handleGroupsChange = (selectedIds) => {
    setSelectedGroupIds(selectedIds);
    setGroupsChanged(true);
  };

  // PBI #373: add-on licences the user may hold, from the customer's own portfolio. A failed
  // fetch leaves the picker out instead of blocking the form, as on onboarding (#300).
  const [addOnLicenses, setAddOnLicenses] = useState([]);
  // null until the customer touches the picker; until then the add-ons are left untouched.
  const [chosenAddOnLicensePublicIds, setChosenAddOnLicensePublicIds] = useState(null);

  const handleGetAddOnLicenses = async () => {
    try {
      const accessToken = await getAccessToken();
      const options = await getAddOnLicenseOptionsByTenant(tenantPublicId, accessToken);

      setAddOnLicenses(options || []);
    } catch (error) {
      console.error("Failed to fetch add-on license options:", error?.message ?? error);
      setAddOnLicenses([]);
    }
  };

  // #363: the account type's own licence is not an add-on.
  const offeredAddOnLicenses = useMemo(() => {
    const baseMpn = accountTypes
      .find((accountType) => accountType.accountTypePublicId === user?.accountTypePublicId)
      ?.licenseMpn?.toLowerCase();

    return addOnLicenses.filter(
      (license) => !baseMpn || license?.mpn?.toLowerCase() !== baseMpn
    );
  }, [addOnLicenses, accountTypes, user?.accountTypePublicId]);

  // The add-ons the user already holds: the user's licences are keyed by SKU id.
  const heldAddOnLicensePublicIds = useMemo(() => {
    const heldSkuIds = Object.keys(user?.licenses || {}).map((skuId) =>
      skuId.toLowerCase()
    );

    return offeredAddOnLicenses
      .filter((license) => heldSkuIds.includes(license.skuId?.toLowerCase()))
      .map((license) => license.licensePublicId);
  }, [offeredAddOnLicenses, user?.licenses]);

  const selectedAddOnLicensePublicIds = (
    chosenAddOnLicensePublicIds ?? heldAddOnLicensePublicIds
  ).filter((id) =>
    offeredAddOnLicenses.some((license) => license.licensePublicId === id)
  );

  const addOnLicensesChanged =
    selectedAddOnLicensePublicIds.length !== heldAddOnLicensePublicIds.length ||
    selectedAddOnLicensePublicIds.some((id) => !heldAddOnLicensePublicIds.includes(id));

  const handleAddOnLicenseChange = (selectedIds) => {
    setChosenAddOnLicensePublicIds(selectedIds);
    setHasChanges(true);
  };

  // Without the saved values the form still offers what the users carry.
  const handleGetScoListValues = async () => {
    try {
      const accessToken = await getAccessToken();
      const values = await getScoFormValues(tenantPublicId, accessToken);

      setScoListValues(values?.listValues || []);
    } catch (error) {
      console.error("Failed to fetch SCO form values:", error?.message ?? error);
    }
  };

  const handleGetScoFormSettings = async () => {
    try {
      const accessToken = await getAccessToken();
      const settings = await getScoFormSettings(accessToken);

      setScoFormSettings(settings || {});
    } catch (error) {
      console.error("Failed to fetch SCO form settings:", error);
    }
  };

  // PBI #320: a failed fetch means no questions are shown — never a blocked form.
  const handleGetCustomQuestions = async () => {
    try {
      const accessToken = await getAccessToken();
      const questions = await getScoCustomQuestions(tenantPublicId, accessToken);

      setCustomQuestions(questions || []);
    } catch (error) {
      console.error("Failed to fetch SCO custom questions:", error?.message ?? error);
    }
  };

  const handleCustomAnswerChange = (questionPublicId, value) => {
    setCustomAnswers((prev) => ({ ...prev, [questionPublicId]: value }));
    setHasChanges(true);
  };

  const handleGetUsers = async () => {
    try {
      const accessToken = await getAccessToken();
      const tenantUsers = await getUsersByTenant(tenantPublicId, accessToken);

      setUsers(tenantUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleGetAccountTypes = async () => {
    try {
      const accessToken = await getAccessToken();
      const aT = await getAccountTypesByAgreement(agreementPublicId, accessToken);

      setAccountTypes(aT);
    } catch (error) {
      console.error("Failed to fetch account types:", error);
    }
  };

  // The same list values the onboarding form offers (PBI #292), so editing and
  // onboarding cannot drift apart.
  const jobTitleOptions = useMemo(
    () => distinctValues(users, "jobTitle"),
    [users]
  );
  const licensedUsers = useMemo(
    () => users.filter((tenantUser) => !isUnlicensedAccount(tenantUser)),
    [users]
  );
  const officeOptions = useMemo(
    () =>
      offeredListValues(
        "Office",
        scoListValues,
        licensedUsers,
        "officeLocation",
        scoFormSettings.offices
      ),
    [scoListValues, licensedUsers, scoFormSettings]
  );
  const countryOptions = useMemo(
    () =>
      combineOptions(scoFormSettings.countries, distinctValues(users, "country")),
    [users, scoFormSettings]
  );
  const companyOptions = useMemo(
    () =>
      offeredListValues(
        "Company",
        scoListValues,
        licensedUsers,
        "company",
        scoFormSettings.companies
      ),
    [scoListValues, licensedUsers, scoFormSettings]
  );
  const departmentOptions = useMemo(
    () => offeredListValues("Department", scoListValues, licensedUsers, "department"),
    [scoListValues, licensedUsers]
  );

  // Manager choices carry the UPN in the label so two people with the same name stay
  // distinguishable — the lookup below is by the whole label, never by name alone.
  const managerChoices = useMemo(
    () =>
      users
        .filter((candidate) => candidate.userPublicId !== userPublicId)
        .filter((candidate) => candidate.displayName)
        .map((candidate) => ({
          label: candidate.userPrincipalName
            ? `${candidate.displayName} (${candidate.userPrincipalName})`
            : candidate.displayName,
          userPublicId: candidate.userPublicId,
          displayName: candidate.displayName,
        })),
    [users, userPublicId]
  );

  const handleGetUser = async () => {
    try {
      const accessToken = await getAccessToken();
      const newUser = await getUser(userPublicId, accessToken);

      if (Array.isArray(newUser.roles)) {
        newUser.roles = newUser.roles.map((r) =>
        typeof r === "string" ? r : r.name
        );        
      } else {
        newUser.roles = [];
      }
      
      setUser(newUser);
      setLoadingUser(false);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setLoadingUser(false);
    }
  };

  const handleGetBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByUser(userPublicId, accessToken);
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleGetRoles = async () => {
    try {
      const accessToken = await getAccessToken();
      const roles = await getAllRoles(accessToken);
      setRoles(roles);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  const handleBillingGroupChange = (option) => {
    const selectedGroup = billingGroups.find((group) => group.name === option);
    if (!selectedGroup) return;
    setUser((prevUser) => ({
      ...prevUser,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
    }));
    setHasChanges(true);
  };

  const handleRoleChange = (option) => {
    const selectedRole = roles.find((group) => group.name === option);
    if (!selectedRole) return;
    setUser((prevUser) => ({
      ...prevUser,
      roleName: selectedRole.roleName,
    }));
    setHasChanges(true);
  };

  // PBI #316: the manager is chosen among the customer's current users. The public id is
  // what the API needs — it resolves the Entra object id server-side.
  const handleManagerChange = (option) => {
    const selectedManager = managerChoices.find(
      (candidate) => candidate.label === option
    );
    if (!selectedManager) return;
    setManagerUserPublicId(selectedManager.userPublicId);
    setUser((prevUser) => ({
      ...prevUser,
      managerDisplayName: selectedManager.displayName,
    }));
    setHasChanges(true);
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

  const handleUpdateUser = async () => {
    // The ref, not the state, is what blocks a second click: React batches state updates, so
    // two clicks in the same tick would both see isSubmitting as false. The state drives the
    // button.
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();

      if (!user.billingGroupPublicId) {
        console.error("Missing billingGroupPublicId! API call might fail.");
        return;
      }

      if (user.billingGroupPublicId) {
        const updatedUser = await updateDipUser(
          user.billingGroupPublicId,
          user.roles,
          user.userPublicId,
          accessToken,
          // PBI #316: the details the customer may change. Sent as empty strings rather
          // than undefined when cleared, so clearing a value is distinguishable from
          // not sending the field at all. The manager is sent only when a
          // choice was made — it cannot be cleared from here.
          {
            jobTitle: user.jobTitle ?? "",
            officeLocation: user.officeLocation ?? "",
            country: user.country ?? "",
            mobilePhone: user.mobilePhone ?? "",
            phone: user.phone ?? "",
            company: user.company ?? "",
            department: user.department ?? "",
            ...(managerUserPublicId && { managerUserPublicId }),
            ...(groupsChanged && { entraGroupPublicIds: selectedGroupIds }),
            // PBI #373: the whole wanted add-on set, sent only when it differs from what
            // the user holds — an omitted field leaves the add-ons untouched.
            ...(addOnLicensesChanged && {
              addOnLicensePublicIds: selectedAddOnLicensePublicIds,
            }),

            // PBI #320: only answers actually given in this edit, keyed by question
            // public id, raw values (boolean for a checkbox). The API resolves the
            // labels and puts the answers in the verification mail.
            ...(Object.keys(customAnswers).length > 0 && {
              customQuestions: Object.fromEntries(
                Object.entries(customAnswers).filter(
                  ([, value]) =>
                    value !== "" && value !== null && value !== undefined
                )
              ),
            }),
          }
        );

        setUser(updatedUser);
      }

      // PBI #320: answers were delivered in the verification mail; clearing them keeps
      // a second save from re-sending the same answers.
      setCustomAnswers({});

      setHasChanges(false); 

      handleGetUser();

      toast.success("User updated successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error("Failed to update user");
    } finally {
      // Also covers the early return inside the try when the billing group is missing,
      // which would otherwise leave the button disabled for good.
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
    navigate(-1);
  };

  const saveAndExit = () => {
    handleUpdateUser();
  };

  if (loaded && !enabled) {
    return <Navigate to={`/${agreementPublicId}/${tenantPublicId}/users`} replace />;
  }

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Users"} loading={loadingUser} />
          </div>
          <div className={`${loadingUser ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit user: {user?.displayName}
              </h2>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <UserForm
                user={user || {}}
                setUser={setUser}
                setHasChanges={setHasChanges}
                disabled={true}
                billingGroups={billingGroups}
                roles={roles}
                onBillingGroupChange={handleBillingGroupChange}
                onRoleChange={handleRoleChange}
                protectedFieldHelpText={scoFormSettings.protectedFieldHelpText || ""}
                managerOptions={managerChoices.map((candidate) => candidate.label)}
                accountTypes={accountTypes}
                accountTypePublicId={user?.accountTypePublicId || ""}
                onManagerChange={handleManagerChange}
                scoFormSettings={scoFormSettings}
                jobTitleOptions={jobTitleOptions}
                officeOptions={officeOptions}
                countryOptions={countryOptions}
                companyOptions={companyOptions}
                departmentOptions={departmentOptions}
                customQuestions={customQuestions}
                customAnswers={customAnswers}
                onCustomAnswerChange={handleCustomAnswerChange}
                groupOptions={groupOptions}
                selectedGroupIds={selectedGroupIds}
                onGroupsChange={handleGroupsChange}
                addOnLicenses={offeredAddOnLicenses}
                addOnLicensePublicIds={selectedAddOnLicensePublicIds}
                onAddOnLicenseChange={handleAddOnLicenseChange}
              />
              <div className="flex justify-end gap-4 pt-4">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges || isSubmitting}
                  onClick={saveAndExit}
                >
                  {isSubmitting ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditUserPage;
